import { ICustomer, ISupplier } from '../dashboard.types'
import { IFilterEntity, IFilterUnit, SORT_FILTERS } from '../../../constants'
import { getDefaultSortedCustomers, customerActivityAt } from '../../../utils/dueDate'
// Suppliers: legacy Android's sortSuppliers falls back to createdAt when lastActivity is
// unset (`lastActivity.epochMillis.takeIf { it > 0 } ?: createdAt.epochMillis`).
const bySupplierRecencyDesc = <T extends { lastActivityAt?: number; createdAt?: number }>(
  a: T,
  b: T
) => (b.lastActivityAt || b.createdAt || 0) - (a.lastActivityAt || a.createdAt || 0)

// Customers: floors at createdAt, matching the `ELSE Customer.createdAt` branch of Android's
// lastActivity SQL — see customerActivityAt.
const byCustomerRecencyDesc = <T extends { lastActivityAt?: number; createdAt?: number }>(
  a: T,
  b: T
) => customerActivityAt(b) - customerActivityAt(a)

// CASE-SENSITIVE, code-unit comparison — deliberately NOT localeCompare, and deliberately
// different from the NAME sort below, which uppercases. The two mirror two different Android
// mechanisms and must not be unified:
//   NAME sort  -> Android's explicit `sortedBy { it.description.lowercase() }`, case-INsensitive
//   this one   -> the row order SQLite hands the sorter, BINARY collation, case-SENSITIVE
// Measured on the prod app: tied rows come back "ACHAL PODDAR", "AMIT CAR", "Aa Ajin", …,
// "fyc", "testtt" — uppercase before lowercase (C=67, M=77 < a=97). localeCompare interleaves
// those and would silently break parity.
const byNameAsc = <T extends { description?: string }>(a: T, b: T) => {
  const nameA = a.description || ''
  const nameB = b.description || ''
  if (nameA < nameB) {
    return -1
  }
  if (nameA > nameB) {
    return 1
  }
  return 0
}

// Android's sorters receive an already-name-ordered list from customerRepo.listCustomers(),
// and both platforms sort stably — so on Android every tie silently falls back to alphabetical.
// Web receives the customer API's order instead, which is why 21 no-payment customers and 12
// zero-balance customers came out in a different order under Last Payment / Due Amount.
//
// Normalising here rather than adding a tie-break to each comparator covers every sort at once,
// including Default and Latest Activity, whose ties simply didn't occur in the data we compared.
// It also removes an order-dependence of web's own: SAVE_FILTERS re-sorts `customers.filtered`,
// which is already ordered by the PREVIOUS sort, so ties could otherwise shift depending on
// which sort was selected before.
const inNameOrder = (customers?: ICustomer[]): ICustomer[] | undefined =>
  customers?.slice().sort(byNameAsc)

export const applyFilters = (
  filters: IFilterEntity[],
  customers?: ICustomer[],
  suppliers?: ISupplier[]
) => {
  const selectedSort = filters[0].entries.find((entry: IFilterUnit) => entry.selected)?.id
  // const selectedFilter = filters[1].entries.find((entry: IFilterUnit) => entry.selected)?.id)
  // Suppliers are left on their incoming order — only the customer tie order was measured
  // against the device; the supplier list comes from a different (KMP) store.
  const byName = inNameOrder(customers)
  if (selectedSort !== undefined) {
    switch (selectedSort) {
      case SORT_FILTERS.DEFAULT: {
        // Due-today → overdue → rest (each by recency). Suppliers have no due date,
        // so they fall back to plain recency — identical to "Latest".
        const sortedCustomers = byName ? getDefaultSortedCustomers(byName) : []
        const sortedSuppliers = suppliers?.slice().sort(bySupplierRecencyDesc)
        return {
          customers: sortedCustomers,
          suppliers: sortedSuppliers || []
        }
      }
      case SORT_FILTERS.NAME: {
        const sortedCustomers = byName
          ?.slice()
          .sort((customer1: ICustomer, customer2: ICustomer) => {
            if (customer1.description.toUpperCase() < customer2.description.toUpperCase()) {
              return -1
            }
            if (customer1.description.toUpperCase() > customer2.description.toUpperCase()) {
              return 1
            }
            return 0
          })
        const sortedSuppliers = suppliers
          ?.slice()
          .sort((supplier1: ISupplier, supplier2: ISupplier) => {
            if (supplier1.name.toUpperCase() < supplier2.name.toUpperCase()) {
              return -1
            }
            if (supplier1.name.toUpperCase() > supplier2.name.toUpperCase()) {
              return 1
            }
            return 0
          })
        return {
          customers: sortedCustomers || [],
          suppliers: sortedSuppliers || []
        }
      }
      case SORT_FILTERS.AMOUNT: {
        const sortedCustomers = byName
          ?.slice()
          .sort(
            (customer1: ICustomer, customer2: ICustomer) => customer1.balance - customer2.balance
          )
        const sortedSuppliers = suppliers
          ?.slice()
          .sort(
            (supplier1: ISupplier, supplier2: ISupplier) =>
              Number(supplier1.balance) - Number(supplier2.balance)
          )
        return {
          customers: sortedCustomers || [],
          suppliers: sortedSuppliers || []
        }
      }
      case SORT_FILTERS.DUE_DATE: {
        // "Latest Activity" — legacy Android's SortOption.LAST_ACTIVITY. Customers sort on
        // lastActivity alone (no activity → bottom); suppliers keep the createdAt fallback,
        // matching the two different Android implementations.
        const sortedCustomers = byName?.slice().sort(byCustomerRecencyDesc)
        const sortedSuppliers = suppliers?.slice().sort(bySupplierRecencyDesc)
        return {
          customers: sortedCustomers || [],
          suppliers: sortedSuppliers || []
        }
      }
      case SORT_FILTERS.LAST_PAYMENT: {
        const sortedCustomers = byName
          ?.slice()
          .sort((a: ICustomer, b: ICustomer) => (b.lastPaymentAt || 0) - (a.lastPaymentAt || 0))
        // Web's supplier formatter doesn't expose lastPayment; suppliers are returned
        // unchanged for this sort.
        return {
          customers: sortedCustomers || [],
          suppliers: suppliers || []
        }
      }
      default: {
        return {
          customers: customers || [],
          suppliers: suppliers || []
        }
      }
    }
  }
  return {
    customers,
    suppliers
  }
  // TODO(v0): Implement Due Date Filter
  // if (selectedFilter) {
  //   // apply filter
  // }
}

export const applyFiltersOnCustomers = (filters: IFilterEntity[], customers?: ICustomer[]) => {
  const selectedSort = filters[0].entries.find((entry: IFilterUnit) => entry.selected)?.id
  const byName = inNameOrder(customers)
  if (selectedSort !== undefined) {
    switch (selectedSort) {
      case SORT_FILTERS.DEFAULT: {
        return {
          customers: byName ? getDefaultSortedCustomers(byName) : []
        }
      }
      case SORT_FILTERS.NAME: {
        const sortedCustomers = byName
          ?.slice()
          .sort((customer1: ICustomer, customer2: ICustomer) => {
            if (customer1.description.toUpperCase() < customer2.description.toUpperCase()) {
              return -1
            }
            if (customer1.description.toUpperCase() > customer2.description.toUpperCase()) {
              return 1
            }
            return 0
          })
        return {
          customers: sortedCustomers || []
        }
      }
      case SORT_FILTERS.AMOUNT: {
        const sortedCustomers = byName
          ?.slice()
          .sort(
            (customer1: ICustomer, customer2: ICustomer) => customer1.balance - customer2.balance
          )
        return {
          customers: sortedCustomers || []
        }
      }
      case SORT_FILTERS.DUE_DATE: {
        // Legacy-Android parity: lastActivity only, no createdAt fallback.
        const sortedCustomers = byName?.slice().sort(byCustomerRecencyDesc)
        return {
          customers: sortedCustomers || []
        }
      }
      case SORT_FILTERS.LAST_PAYMENT: {
        const sortedCustomers = byName
          ?.slice()
          .sort(
            (customer1: ICustomer, customer2: ICustomer) =>
              (customer2.lastPaymentAt || 0) - (customer1.lastPaymentAt || 0)
          )
        return {
          customers: sortedCustomers || []
        }
      }
      default: {
        return {
          customers: customers || []
        }
      }
    }
  }
  return {
    customers
  }
}
